import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle, MapPin, ArrowRight } from "lucide-react";
import { CONTACT, PROJECT_TYPES, CTA } from "@/data/site";
import { Reveal } from "@/components/site/Reveal";

// The enquiry API is optional. When no backend is configured the form composes
// the same enquiry as an email so submissions still reach the studio.
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const HAS_API = Boolean(BACKEND && BACKEND !== "undefined");

const composeMailto = (form) => {
  const subject = `Project enquiry — ${form.project_type || "General"} — ${form.name}`;
  const body = [
    `Name:    ${form.name}`,
    `Email:   ${form.email}`,
    `Phone:   ${form.phone || "—"}`,
    `Scope:   ${form.project_type || "—"}`,
    "",
    form.message,
  ].join("\n");
  return `mailto:${CONTACT.email}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
};

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Add your name, email and a short note about the project.");
      return;
    }

    setSubmitting(true);
    try {
      if (HAS_API) {
        await axios.post(`${BACKEND}/api/contact`, form);
        toast.success("Enquiry received. We'll come back to you shortly.");
      } else {
        window.location.href = composeMailto(form);
        toast.success("Opening your email client to send the enquiry.");
      }
      setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
    } catch (err) {
      window.location.href = composeMailto(form);
      toast.error(`Couldn't submit directly — opening email to ${CONTACT.email}.`);
    } finally {
      setSubmitting(false);
    }
  };

  const details = [
    {
      icon: Phone,
      label: "Call",
      value: CONTACT.phone,
      href: `tel:${CONTACT.phoneTel}`,
      testid: "contact-phone",
    },
    {
      icon: Mail,
      label: "Email",
      value: CONTACT.email,
      href: `mailto:${CONTACT.email}`,
      testid: "contact-email",
    },
    {
      icon: MessageCircle,
      label: "WhatsApp",
      value: "Message the studio",
      href: CONTACT.whatsapp,
      testid: "contact-whatsapp",
    },
    {
      icon: MapPin,
      label: "Studio",
      value: CONTACT.location,
      href: null,
      testid: "contact-location",
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-obsidian border-t border-ivory/10 py-20 md:py-28"
    >
      <div className="mx-auto max-w-shell px-6 md:px-10">
        <Reveal>
          <div className="text-center">
            <h2 className="font-display font-normal text-ivory leading-[1.05] tracking-[-0.015em] text-[clamp(2rem,5vw,4rem)]">
              {CTA.title}
            </h2>
            <div className="mt-6 label-xs text-champagne">{CTA.corridor}</div>
          </div>
        </Reveal>

        <div className="mt-16 md:mt-20 grid md:grid-cols-12 gap-14 md:gap-16">
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="md:col-span-7 flex flex-col gap-9"
          >
            <div className="grid sm:grid-cols-2 gap-9">
              <div>
                <label className="label-xs text-mist" htmlFor="c-name">
                  Name
                </label>
                <input
                  id="c-name"
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Your full name"
                  className="field-underline w-full mt-3 pb-3 text-lg"
                />
              </div>
              <div>
                <label className="label-xs text-mist" htmlFor="c-email">
                  Email
                </label>
                <input
                  id="c-email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@brand.com"
                  className="field-underline w-full mt-3 pb-3 text-lg"
                />
              </div>
            </div>

            <div>
              <label className="label-xs text-mist" htmlFor="c-phone">
                Phone (optional)
              </label>
              <input
                id="c-phone"
                data-testid="contact-input-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="647 000 0000"
                className="field-underline w-full mt-3 pb-3 text-lg"
              />
            </div>

            <div>
              <span className="label-xs text-mist">Scope</span>
              <div className="mt-4 flex flex-wrap gap-3">
                {PROJECT_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`project-type-${t.toLowerCase().replace(/[^a-z]/g, "")}`}
                    onClick={() =>
                      update("project_type", form.project_type === t ? "" : t)
                    }
                    className={`px-5 py-2.5 label-xs border transition-all duration-300 ${
                      form.project_type === t
                        ? "border-champagne bg-champagne text-obsidian"
                        : "border-ivory/25 text-ivory/70 hover:border-champagne hover:text-champagne"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label-xs text-mist" htmlFor="c-message">
                The project
              </label>
              <textarea
                id="c-message"
                data-testid="contact-input-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={4}
                placeholder="Brand, location, number of doors, and when it has to be trading."
                className="field-underline w-full mt-3 pb-3 text-lg resize-none"
              />
            </div>

            <motion.button
              type="submit"
              data-testid="contact-submit-button"
              disabled={submitting}
              whileTap={{ scale: 0.98 }}
              className="group inline-flex items-center justify-center gap-3 bg-champagne text-obsidian px-10 py-5 label-xs hover:bg-ivory transition-colors duration-400 disabled:opacity-50 w-full sm:w-auto"
            >
              {submitting ? "Sending…" : CTA.action}
              <ArrowRight
                size={15}
                className="group-hover:translate-x-1 transition-transform"
              />
            </motion.button>
          </form>

          <div className="md:col-span-5 md:border-l md:border-ivory/12 md:pl-12">
            <div className="flex flex-col divide-y divide-ivory/12 border-y border-ivory/12">
              {details.map((d) => {
                const Inner = (
                  <div className="flex items-center gap-5 py-6 group">
                    <span className="h-11 w-11 flex items-center justify-center border border-ivory/20 text-champagne group-hover:bg-champagne group-hover:text-obsidian transition-all duration-400">
                      <d.icon size={17} strokeWidth={1.5} />
                    </span>
                    <div>
                      <div className="label-xs text-mist">{d.label}</div>
                      <div className="mt-1.5 text-ivory text-base font-light">
                        {d.value}
                      </div>
                    </div>
                  </div>
                );
                return d.href ? (
                  <a
                    key={d.label}
                    href={d.href}
                    target={d.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-testid={d.testid}
                  >
                    {Inner}
                  </a>
                ) : (
                  <div key={d.label} data-testid={d.testid}>
                    {Inner}
                  </div>
                );
              })}
            </div>

            <div className="mt-8 label-xs text-slate">{CONTACT.note}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
